/**
 * Regras puras da tela Relatórios › Financeiro › Fornecedores. A conciliação
 * em si vem pronta do backend (flags); aqui só se decide como mostrá-la.
 */
import type {
  DueDateShift,
  SupplierAgendaItem,
  SupplierAgendaSituation,
  SupplierForecastGroup,
  SupplierForecastItem,
  SupplierMatchRule,
  SupplierReportDocument,
  SupplierReportFlag,
  SupplierReportRow,
} from '@/types/financialReports';

export interface FlagStyle {
  label: string;
  /** Classes Tailwind do badge. */
  cls: string;
  title: string;
}

/** Ordem = gravidade: o primeiro que aparecer é o badge principal. */
export const FLAG_PRIORITY: SupplierReportFlag[] = [
  'duplicado',
  'valor_divergente',
  'vencida',
  'sem_lancamento',
  'sem_pc',
  'pc_cancelado',
  'total_difere',
];

export const FLAG_STYLES: Record<SupplierReportFlag, FlagStyle> = {
  duplicado: {
    label: 'Duplicado',
    cls: 'bg-violet-100 text-violet-700',
    title: 'Mais lançamentos do que parcelas no pedido — provavelmente a mesma parcela provisionada duas vezes',
  },
  valor_divergente: {
    label: 'Valor divergente',
    cls: 'bg-red-100 text-red-700',
    title: 'Parcela e lançamento pareados com valores diferentes',
  },
  vencida: {
    label: 'Vencida',
    cls: 'bg-red-100 text-red-700',
    title: 'Parcela vencida sem lançamento no Conta Corrente',
  },
  sem_lancamento: {
    label: 'Sem lançamento',
    cls: 'bg-amber-100 text-amber-800',
    title: 'Parcela do pedido sem lançamento correspondente no Conta Corrente',
  },
  sem_pc: {
    label: 'Sem pedido',
    cls: 'bg-sky-100 text-sky-700',
    title: 'Pagamento com fornecedor mas sem pedido de compra/recebimento vinculado',
  },
  pc_cancelado: {
    label: 'PC cancelado',
    cls: 'bg-slate-200 text-slate-700',
    title: 'Lançamento vinculado a um pedido cancelado ou inativo',
  },
  total_difere: {
    label: 'Parcelas ≠ total',
    cls: 'bg-slate-100 text-slate-600',
    title: 'A soma das parcelas difere do total do pedido (a parcela é o que vale para o pagamento)',
  },
};

/**
 * Como o par parcela × lançamento foi decidido. "posicao" é a que merece
 * atenção: sobrou uma parcela e um lançamento e o sistema os juntou por ordem,
 * sem o valor bater.
 */
export const MATCH_RULE_LABELS: Record<Exclude<SupplierMatchRule, ''>, { label: string; title: string }> = {
  valor_data: {
    label: 'mesmo valor, data próxima',
    title: 'Lançamento com o mesmo valor da parcela e data até 45 dias do vencimento',
  },
  valor: {
    label: 'mesmo valor',
    title: 'Lançamento com o mesmo valor da parcela, mas em data distante do vencimento',
  },
  posicao: {
    label: 'pareado por ordem',
    title: 'Nenhum lançamento tem o valor da parcela: o sistema juntou a próxima parcela sem par com o próximo lançamento sem par. Confira se é o mesmo pagamento.',
  },
};

/** Badge principal de um conjunto de flags (o mais grave), ou null se "ok". */
export const primaryFlag = (flags: SupplierReportFlag[]): SupplierReportFlag | null => {
  for (const f of FLAG_PRIORITY) {
    if (flags.includes(f)) return f;
  }
  return null;
};

/** Flags ordenadas por gravidade, sem repetição. */
export const sortedFlags = (flags: SupplierReportFlag[]): SupplierReportFlag[] =>
  FLAG_PRIORITY.filter(f => flags.includes(f));

/** Chave estável da linha do fornecedor (null = não identificado). */
export const supplierKey = (providerId: number | null): string =>
  providerId == null ? 'none' : String(providerId);

/** Chave estável do documento dentro do fornecedor. */
export const documentKey = (providerId: number | null, doc: SupplierReportDocument): string =>
  `${supplierKey(providerId)}:${doc.source}:${doc.documentId}`;

/** Total lançado (pago + a pagar) da linha. */
export const bookedTotal = (row: Pick<SupplierReportRow, 'realized' | 'planned'>): number =>
  row.realized + row.planned;

/**
 * Sub-rótulo da célula da matriz: o que da célula ainda está "só no pedido"
 * (parcela sem lançamento) e o que já foi pago. Vazio quando a célula é 100%
 * lançada e ainda a pagar — o caso comum, que não precisa de nota.
 */
export const forecastCellNote = (
  amount: number,
  orderOnly: number,
  realized: number,
  fmt: (v: number) => string,
): string => {
  if (amount === 0) return '';
  const parts: string[] = [];
  if (orderOnly > 0 && orderOnly >= amount) parts.push('só no pedido');
  else if (orderOnly > 0) parts.push(`${fmt(orderOnly)} só no pedido`);
  if (realized > 0 && realized >= amount) parts.push('pago');
  else if (realized > 0) parts.push(`${fmt(realized)} pago`);
  return parts.join(' · ');
};

/** Itens do drill-down de um mês (índice) da linha do fornecedor. */
export const forecastItemsForMonth = (group: SupplierForecastGroup, monthIndex: number): SupplierForecastItem[] =>
  group.items.filter(i => i.monthIndex === monthIndex);

/** Há alguma coluna "Em atraso" para mostrar? */
export const forecastHasOverdue = (groups: SupplierForecastGroup[]): boolean =>
  groups.some(g => g.overdue > 0);

/** Rótulo curto da fonte do item da previsão. */
export const forecastSourceLabel = (item: SupplierForecastItem): string =>
  item.source === 'parcela' ? 'só no pedido' : item.realized ? 'pago' : 'a pagar';

// ─── Vencimento em dia útil / agenda ────────────────────────────

export const DUE_DATE_SHIFT_LABEL: Record<DueDateShift, string> = {
  sabado: 'sábado',
  domingo: 'domingo',
  feriado: 'feriado',
};

const WEEKDAYS_SHORT = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

/**
 * Dia da semana abreviado de um ISO "yyyy-MM-dd". O Date é construído pelos
 * componentes (nunca `new Date(iso)`, que é UTC e volta um dia no fuso −3).
 */
export const weekdayShortBR = (iso: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return '';
  return WEEKDAYS_SHORT[new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getDay()];
};

/**
 * Explicação do deslocamento: "sáb 19/09 (sábado) → seg 21/09". Vazio quando
 * o vencimento já era dia útil.
 */
export const dueDateShiftLabel = (
  original: string | undefined,
  effective: string,
  shift: DueDateShift | undefined,
  fmt: (iso: string) => string,
): string => {
  if (!original || !shift) return '';
  return `${weekdayShortBR(original)} ${fmt(original)} (${DUE_DATE_SHIFT_LABEL[shift]}) → ${weekdayShortBR(effective)} ${fmt(effective)}`;
};

export interface AgendaDay {
  date: string;
  items: SupplierAgendaItem[];
  total: number;
  paid: number;
  open: number;
}

/** Agrupa a agenda (já ordenada pelo backend) por dia de vencimento efetivo. */
export const groupAgendaByDay = (agenda: SupplierAgendaItem[]): AgendaDay[] => {
  const days: AgendaDay[] = [];
  for (const item of agenda) {
    let day = days[days.length - 1];
    if (!day || day.date !== item.dueDate) {
      day = { date: item.dueDate, items: [], total: 0, paid: 0, open: 0 };
      days.push(day);
    }
    day.items.push(item);
    day.total += item.value;
    if (item.situation === 'pago') day.paid += item.value;
    else day.open += item.value;
  }
  return days;
};

export interface AgendaTotals {
  count: number;
  total: number;
  paid: number;
  open: number;
  overdue: number;
}

/** Totais da agenda inteira (para os chips de filtro). */
export const agendaTotals = (agenda: SupplierAgendaItem[]): AgendaTotals => {
  const t: AgendaTotals = { count: agenda.length, total: 0, paid: 0, open: 0, overdue: 0 };
  for (const item of agenda) {
    t.total += item.value;
    if (item.situation === 'pago') t.paid += item.value;
    else if (item.situation === 'vencida') t.overdue += item.value;
    else t.open += item.value;
  }
  return t;
};

export const AGENDA_SITUATION_META: Record<SupplierAgendaSituation, { label: string; cls: string }> = {
  pago: { label: 'pago', cls: 'bg-emerald-50 text-emerald-700' },
  a_pagar: { label: 'a pagar', cls: 'bg-slate-100 text-slate-600' },
  vencida: { label: 'vencida', cls: 'bg-red-100 text-red-700' },
};
